/**
 * Plain-language copy for Firebase Auth failures.
 *
 * Firebase errors carry a stable `code` ("auth/wrong-password") and an
 * English `message` written for developers ("Firebase: Error
 * (auth/wrong-password)."). Only the code is read here; the message never
 * reaches the screen. Credential failures share one sentence so the page
 * doesn't reveal whether an account exists for an address.
 */

export type AuthErrorContext =
  | 'sign-in'
  | 'sign-up'
  | 'editor-registration'
  | 'reset-request'
  | 'reset-password'
  | 'email-verification';

/** The form field a problem belongs to, so it can be shown under that field. */
export type AuthErrorField = 'email' | 'password';

/** A next step to offer next to the message. */
export type AuthErrorAction = 'reset-password' | 'sign-in' | 'contact-support';

export interface AuthErrorCopy {
  message: string;
  severity: 'error' | 'warning';
  field?: AuthErrorField;
  action?: AuthErrorAction;
}

/** The `auth/...` code of a Firebase Auth error, or null for anything else. */
export const getAuthErrorCode = (error: unknown): string | null => {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' && code.startsWith('auth/') ? code : null;
};

const FALLBACK_MESSAGES: Record<AuthErrorContext, string> = {
  'sign-in': "We couldn't sign you in. Try again, or contact support if it keeps happening.",
  'sign-up': "We couldn't create your account. Try again, or contact support if it keeps happening.",
  'editor-registration':
    "We couldn't finish creating your account. Please try again, or contact support if it keeps happening.",
  'reset-request': "We couldn't send the reset link. Try again in a few minutes.",
  'reset-password': "We couldn't update your password. Try again, or request a new reset link.",
  'email-verification': "We couldn't send the verification email. Try again in a few minutes.",
};

const TOO_MANY_MESSAGES: Record<AuthErrorContext, string> = {
  'sign-in': 'Too many sign-in attempts. Wait a few minutes, or reset your password to sign in now.',
  'sign-up': 'Too many attempts. Wait a few minutes, then try again.',
  'editor-registration': 'Too many attempts. Please wait a few minutes and try again.',
  'reset-request': 'Too many reset requests. Wait a few minutes, then try again.',
  'reset-password': 'Too many attempts. Wait a few minutes, then try again.',
  'email-verification': 'Too many verification emails requested. Wait a few minutes, then try again.',
};

export const NETWORK_ERROR_MESSAGE = "We couldn't reach PithPlay. Check your connection and try again.";

const NETWORK_FAILURE_PATTERN = /failed to fetch|networkerror|network error|network request failed|load failed/i;

/** A request that never reached the server (offline, DNS, CORS), from Firebase or fetch. */
export const isNetworkFailure = (error: unknown): boolean => {
  if (getAuthErrorCode(error) === 'auth/network-request-failed') return true;
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && NETWORK_FAILURE_PATTERN.test(message);
};

/** Customer-facing copy for an auth failure in the given context. */
export const describeAuthError = (error: unknown, context: AuthErrorContext): AuthErrorCopy => {
  const code = getAuthErrorCode(error);

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      if (context === 'sign-in') {
        return {
          message: "That email and password don't match. Try again, or reset your password.",
          severity: 'error',
          action: 'reset-password',
        };
      }
      break;

    case 'auth/invalid-email':
      if (context === 'editor-registration') {
        return {
          message: "The email address on this invitation isn't valid. Ask the creator to send you a new invitation.",
          severity: 'error',
        };
      }
      return { message: 'Enter a valid email address.', severity: 'error', field: 'email' };

    case 'auth/missing-password':
      return { message: 'Enter your password.', severity: 'error', field: 'password' };

    case 'auth/weak-password':
      return {
        message: 'Choose a stronger password that meets every requirement below.',
        severity: 'error',
        field: 'password',
      };

    case 'auth/user-disabled':
      return {
        message: 'This account has been suspended. Contact support for help.',
        severity: 'error',
        action: 'contact-support',
      };

    case 'auth/too-many-requests':
      return {
        message: TOO_MANY_MESSAGES[context],
        severity: 'warning',
        action: context === 'sign-in' ? 'reset-password' : undefined,
      };

    case 'auth/network-request-failed':
      return { message: NETWORK_ERROR_MESSAGE, severity: 'error' };

    case 'auth/email-already-in-use':
      if (context === 'editor-registration') {
        return {
          message: "You've already registered with this email. Sign in instead.",
          severity: 'warning',
          action: 'sign-in',
        };
      }
      return {
        message: 'An account with this email already exists. Sign in instead, or use a different email.',
        severity: 'warning',
        action: 'sign-in',
      };

    case 'auth/operation-not-allowed':
      return {
        message:
          context === 'sign-up' || context === 'editor-registration'
            ? "New accounts can't be created right now. Try again later, or contact support."
            : "This isn't available right now. Contact support for help.",
        severity: 'error',
        action: 'contact-support',
      };

    case 'auth/expired-action-code':
      return { message: 'This link has expired. Request a new one.', severity: 'error' };

    case 'auth/invalid-action-code':
      return { message: "This link isn't valid or has already been used. Request a new one.", severity: 'error' };

    case 'auth/internal-error':
    case 'auth/service-unavailable':
      return { message: 'Something went wrong on our side. Try again in a few minutes.', severity: 'error' };

    case 'auth/admin-restricted-operation':
      return {
        message: "New accounts can't be created right now. Contact support and we'll set yours up.",
        severity: 'error',
        action: 'contact-support',
      };

    case 'auth/password-does-not-meet-requirements':
      return { message: 'Choose a longer, stronger password.', severity: 'error', field: 'password' };

    default:
      break;
  }

  if (!code && isNetworkFailure(error)) {
    return { message: NETWORK_ERROR_MESSAGE, severity: 'error' };
  }
  // The code lets support see what actually happened.
  const reference = code ? ` Error code: ${code.replace(/^auth\//, '')}.` : '';
  return { message: `${FALLBACK_MESSAGES[context]}${reference}`, severity: 'error', action: 'contact-support' };
};
